import { Realtime } from 'ably';
import type { Auth } from './auth';
import { inject } from 'vue';
import type { App, Plugin } from 'vue';
import logger from '@/logger';
import type { PowerDialerApis } from './apis';
import { fetchUser } from './use-user';

const log = logger.getChild('pub-sub');
const DEFAULT_INJECTION_KEY = Symbol('powerdialer-pub-sub');

const pubSubPlugin: Plugin<{ pubSub: PubSub }> = {
  install(app, { pubSub }) {
    log.log('installing pubSub plugin');
    app.provide(DEFAULT_INJECTION_KEY, pubSub);
  },
};

const connectAbly = async ({
  pubSub,
  token,
  companyId,
  POWER_DIALER_URL,
  ABLY_REALTIME_HOST,
  ABLY_FALLBACK_HOSTS,
}: {
  pubSub: PubSub;
  token: string;
  companyId: string;
  POWER_DIALER_URL: string;
  ABLY_REALTIME_HOST: string;
  ABLY_FALLBACK_HOSTS: string[];
}): Promise<Realtime> => {
  const ably = new Realtime({
    authUrl: `${POWER_DIALER_URL}/api/ably`,
    authHeaders: {
      Authorization: token,
    },
    /**
     * 0 - no logging
     * 1 - error logging
     * 2 - connection state changes
     * 3 - abbreviated debug output
     * 4 - full debug output
     */
    logLevel: 3,
    logHandler: (msg: string, level: number) => {
      switch (level) {
        case 1:
          log.warn('[Ably]', msg);
          break;
        case 2:
          log.log('[Ably]', msg);
          break;
        default:
          log.debug('[Ably]', msg);
      }
    },
    realtimeHost: ABLY_REALTIME_HOST,
    fallbackHosts: ABLY_FALLBACK_HOSTS,
    // ably fires some unhandled error events to sentry
    // since we don't use connection state anywhere, disabling this
    closeOnUnload: false,
  });
  ably.connection.on((stateChange) => {
    switch (stateChange.current) {
      case 'disconnected':
        log.log(`Ably disconnected:`, ably.connection.errorReason);
        break;
      default:
        log.log(`Ably connection state:`, stateChange.current);
    }
  });

  const channelId = `cid_${companyId}`;
  log.log('subscribing to channel:', channelId);
  const channel = ably.channels.get(channelId);

  void channel.subscribe('message', (message) => {
    const data = message.data;
    const type = data?.type;
    const msg = data?.msg;

    switch (type) {
      case 'err':
        log.error('server error:', msg.err);
        break;
      case 'key':
        pubSub.update(msg);
        break;
      case 'del':
        pubSub.delete(msg);
        break;
      // we are not interested in these messages:
      case 'call':
      case 'sms':
        break;
      default:
        log.warn('unknown message type:', type, 'msg:', msg);
    }
  });

  return ably;
};

export const hostFakePubSub = async ({
  vueApp,
}: {
  vueApp: App;
  apis: PowerDialerApis;
}): Promise<void> => {
  log.log('hosting fake pubSub');
  const pubSub = new PubSub();
  vueApp.use(pubSubPlugin, { pubSub });
  return;
};

export const hostPubSub = async ({
  env: { POWER_DIALER_URL, ABLY_REALTIME_HOST, ABLY_FALLBACK_HOSTS },
  auth,
  vueApp,
  apis,
}: {
  env: {
    POWER_DIALER_URL: string;
    ABLY_REALTIME_HOST: string;
    ABLY_FALLBACK_HOSTS: string[];
  };
  auth: Auth;
  vueApp: App;
  apis: PowerDialerApis;
}): Promise<void> => {
  log.log('hosting pubSub');
  const pubSub = new PubSub();
  vueApp.use(pubSubPlugin, { pubSub });

  void Promise.resolve().then(async () => {
    await auth.authController.waitForIdb();
    await auth.authController.waitForAuthorized();

    const token = auth.authController.getToken();
    const user = await fetchUser({
      authController: auth.authController,
      dpOAuth: auth.dpOAuth,
      contactApi: apis.contactApi,
    });

    if (!user) {
      log.log('no user, skipping pubSub');
      return;
    }

    if (!user.company?.id) {
      log.error('company_id not found in auth state');
      return;
    }

    if (!token) {
      log.error('token not found in auth state');
      return;
    }

    void connectAbly({
      pubSub,
      token,
      companyId: user.company.id.toString(),
      POWER_DIALER_URL,
      ABLY_REALTIME_HOST,
      ABLY_FALLBACK_HOSTS,
    });
  });
};

export const usePubSub = (): PubSub => {
  const pubSub = inject<PubSub>(DEFAULT_INJECTION_KEY);

  if (!pubSub) {
    throw new Error('usePubSub must be used within a vue context');
  }

  return pubSub;
};

export class PubSub {
  private updateSubscribers: Map<
    string,
    ((key: string) => void)[] | undefined
  > = new Map();
  private deleteSubscribers: Map<
    string,
    ((key: string) => void)[] | undefined
  > = new Map();
  private cacheUpdateSubscribers: Map<
    string,
    ((key: string, entity: unknown) => void)[] | undefined
  > = new Map();

  update(key: string): void {
    const [type, id] = key.split(':');
    const callbacks = this.updateSubscribers.get(type);
    if (callbacks) {
      callbacks.forEach((callback) => {
        callback(id);
      });
    }
  }

  private addListener(
    map: Map<string, ((key: string, data?: unknown) => void)[] | undefined>,
    type: string,
    callback: (id: string, data?: unknown) => void,
  ): () => void {
    if (!map.has(type)) {
      map.set(type, [callback]);
    } else {
      map.get(type)?.push(callback);
    }

    return () => {
      const callbacks = map.get(type);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index !== -1) {
          callbacks.splice(index, 1);
        }
        if (callbacks.length === 0) {
          map.delete(type);
        }
      }
    };
  }

  onUpdate(type: string, callback: (id: string) => void): () => void {
    return this.addListener(this.updateSubscribers, type, callback);
  }

  delete(key: string): void {
    const [type, id] = key.split(':');
    const callbacks = this.deleteSubscribers.get(type);
    if (callbacks) {
      callbacks.forEach((callback) => {
        callback(id);
      });
    }
  }

  onDelete(type: string, callback: (id: string) => void): () => void {
    return this.addListener(this.deleteSubscribers, type, callback);
  }

  // these are not real pub-sub methods that initiated by backend, but rather
  // a way to allow stores to notify other stores about changes

  cacheUpdate(key: string, entity: unknown): void {
    const [type, id] = key.split(':');
    const callbacks = this.cacheUpdateSubscribers.get(type);
    if (callbacks) {
      callbacks.forEach((callback) => {
        callback(id, entity);
      });
    }
  }

  onCacheUpdate(
    type: string,
    callback: (id: string, entity: unknown) => void,
  ): () => void {
    return this.addListener(this.cacheUpdateSubscribers, type, callback);
  }
}
